import { useCallback, useEffect, useRef, useState } from 'react';
import useAnalytics from '@/hooks/useAnalytics';
import type { CountyPreviewSource } from '@/hooks/useGuestAddress';
import {
  getDevicePosition,
  PRECISE_FIX_METERS,
  queryGeolocationPermission,
  type DevicePositionFailure,
  type GeolocationPermission,
} from '@/utils/deviceLocation';
import {
  fetchAddressAt,
  fetchCountyGuess,
  fetchCountyPreview,
  type CountyPreviewAddress,
  type ExactAddress,
  type CountyPreviewFailure,
  type CountyPreviewResult,
} from '@/utils/placesApi';

/**
 * ``hidden``: nothing to offer (unsupported, blocked, or not enabled).
 * ``idle``: show the "use my location" button.
 * ``locating``: finding the device's county.
 * ``failed``: the last attempt failed; ``failure`` says why.
 */
export type CountyPreviewStatus = 'hidden' | 'idle' | 'locating' | 'failed';

interface UseCountyPreviewOptions {
  /** Off for signed-in users, partner embeds, and once an address exists. */
  enabled: boolean;
  /**
   * Without a location the browser already allows, guess the county from the
   * visitor's IP rather than waiting for a tap.
   */
  ipGuess?: boolean;
  /** Called with the public address once found. Return false to discard it. */
  onPreview: (preview: CountyPreviewAddress, source: CountyPreviewSource) => boolean;
  /** Called with the street address at a precise fix. Return false to discard it. */
  onExactAddress: (address: ExactAddress) => boolean;
}

export type DeviceLocationResult =
  | { exact: ExactAddress; preview?: undefined; failure?: undefined }
  | (CountyPreviewResult & { exact?: undefined });

/**
 * The device's position as the street address there when the fix is precise,
 * otherwise (or when no address is found) as a public address in its county.
 * ``precise: false`` for lookups the voter didn't tap for.
 */
export const locateDeviceCounty = async ({
  precise = true,
} = {}): Promise<DeviceLocationResult> => {
  let position;
  try {
    position = await getDevicePosition({ precise });
  } catch (reason) {
    return { preview: null, failure: reason as DevicePositionFailure };
  }
  if (position.accuracy <= PRECISE_FIX_METERS) {
    const exact = await fetchAddressAt(position);
    if (exact) return { exact };
  }
  return fetchCountyPreview(position);
};

// One silent attempt of each kind per page load: the landing remounts as the
// voter moves between surfaces, and a failed lookup shouldn't repeat each time.
let autoAttemptedThisLoad = false;
let ipGuessAttemptedThisLoad = false;

/** Test-only: forget this page load's automatic attempts. */
export const resetCountyPreviewAutoAttempt = () => {
  autoAttemptedThisLoad = false;
  ipGuessAttemptedThisLoad = false;
};

/**
 * Builds a county sample ballot. Uses the device's location silently when the
 * visitor has already granted it; otherwise waits for ``requestLocation`` (a
 * button tap), since browsers ask that a permission prompt follow a user
 * gesture that explains it. With ``ipGuess`` it meanwhile guesses the county
 * from the IP, which a tap on the button always overrides.
 */
const useCountyPreview = ({
  enabled,
  ipGuess = false,
  onPreview,
  onExactAddress,
}: UseCountyPreviewOptions) => {
  const { track } = useAnalytics();
  const [permission, setPermission] = useState<GeolocationPermission | null>(null);
  const [status, setStatus] = useState<CountyPreviewStatus>('hidden');
  const [failure, setFailure] = useState<CountyPreviewFailure | null>(null);
  const onPreviewRef = useRef(onPreview);
  const onExactAddressRef = useRef(onExactAddress);
  const enabledRef = useRef(enabled);
  useEffect(() => {
    onPreviewRef.current = onPreview;
    onExactAddressRef.current = onExactAddress;
    enabledRef.current = enabled;
  });
  // A lookup that resolves after the landing unmounts (the voter skipped it
  // or followed a link) is dropped, so it can't pull them back to chat.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const runningRef = useRef(false);
  // Bumped when a device lookup starts, so a slower IP guess never replaces it.
  const deviceRunsRef = useRef(0);

  const run = useCallback(
    async (grant: 'prior' | 'granted_now', tapped: boolean) => {
      if (runningRef.current) return;
      runningRef.current = true;
      deviceRunsRef.current += 1;
      setStatus('locating');
      setFailure(null);
      try {
        const result = await locateDeviceCounty({ precise: tapped });
        if (!mountedRef.current) return;
        // The voter may have typed an address while this resolved.
        if (!enabledRef.current) {
          setStatus('hidden');
          return;
        }
        if (result.exact) {
          if (!onExactAddressRef.current(result.exact)) {
            setStatus('idle');
            return;
          }
          setPermission('granted');
          setStatus('hidden');
          track('Landing Exact Address Found', { permission: grant });
          return;
        }
        if (!result.preview) {
          const reason = result.failure;
          setFailure(reason);
          setStatus(reason === 'denied' ? 'hidden' : 'failed');
          if (reason === 'denied') setPermission('denied');
          track('Landing County Preview Failed', { reason, source: 'device' });
          return;
        }
        if (!onPreviewRef.current(result.preview, 'device')) {
          setStatus('idle');
          return;
        }
        setPermission('granted');
        setStatus('hidden');
        track('Landing County Preview Shown', {
          state: result.preview.state,
          permission: grant,
          source: 'device',
        });
      } finally {
        runningRef.current = false;
      }
    },
    [track]
  );

  const guessFromIp = useCallback(async () => {
    const deviceRunsAtStart = deviceRunsRef.current;
    const result = await fetchCountyGuess();
    if (!mountedRef.current || !enabledRef.current || deviceRunsRef.current !== deviceRunsAtStart) {
      return;
    }
    if (!result.preview) {
      track('Landing County Preview Failed', { reason: result.failure, source: 'ip' });
      return;
    }
    if (onPreviewRef.current(result.preview, 'ip')) {
      track('Landing County Preview Shown', { state: result.preview.state, source: 'ip' });
    }
  }, [track]);

  useEffect(() => {
    if (!enabled) {
      if (!runningRef.current) setStatus('hidden');
      return;
    }
    let cancelled = false;
    void queryGeolocationPermission().then((state) => {
      if (cancelled) return;
      setPermission(state);
      if (state === 'granted' && !autoAttemptedThisLoad) {
        autoAttemptedThisLoad = true;
        void run('prior', false);
        return;
      }
      if (!runningRef.current) {
        setStatus(state === 'unsupported' || state === 'denied' ? 'hidden' : 'idle');
      }
      if (ipGuess && !ipGuessAttemptedThisLoad) {
        ipGuessAttemptedThisLoad = true;
        void guessFromIp();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, ipGuess, run, guessFromIp]);

  const requestLocation = useCallback(() => {
    track('Landing Location Requested', { permission_state: permission ?? 'prompt' });
    void run(permission === 'granted' ? 'prior' : 'granted_now', true);
  }, [track, run, permission]);

  return { status, failure, requestLocation };
};

export default useCountyPreview;
